/**
 * "A new version is out — reload": the dashboard's build (the git SHA baked in at build time,
 * NEXT_PUBLIC_GIT_SHA) against the build the server is running now. The Next server stamps every
 * response with its SHA (the `x-poseidon-build` header, next.config.ts `headers()`), so the check is
 * a HEAD of a tiny static file — no page render, no API call.
 */

/** Response header carrying the server's build SHA (next.config.ts). */
export const BUILD_HEADER = 'x-poseidon-build';
/** How often an open tab checks. A focus check also runs, at most once per {@link FOCUS_MIN_GAP_MS}. */
export const BUILD_CHECK_INTERVAL_MS = 5 * 60_000;
export const FOCUS_MIN_GAP_MS = 60_000;
/** A small file the Next server serves itself (public/, not the asset CDN), so it carries the header. */
const BUILD_PROBE_PATH = '/favicon-16x16.png';

/** True only when both builds are known and differ — an unknown either side never prompts. */
export function isNewBuild(clientBuild: string | null | undefined, serverBuild: string | null | undefined): boolean {
  if (clientBuild == null || clientBuild === '' || serverBuild == null || serverBuild === '') return false;
  return clientBuild !== serverBuild;
}

/** Whether this tab should watch at all: never in local dev, and only when the build knows its SHA. */
export function shouldWatchBuild(nodeEnv: string, clientBuild: string | null | undefined): boolean {
  return nodeEnv === 'production' && clientBuild != null && clientBuild !== '';
}

/** The server's build SHA, or null when it can't be read (offline, an old server without the header). */
export async function fetchServerBuild(fetchFn: typeof fetch = fetch): Promise<string | null> {
  try {
    // A cache-busting query and no-store, so neither the browser nor a CDN answers with an old copy.
    const res = await fetchFn(`${BUILD_PROBE_PATH}?build-check=${Date.now()}`, { method: 'HEAD', cache: 'no-store' });
    return res.headers.get(BUILD_HEADER);
  } catch {
    return null;
  }
}

/**
 * Check every {@link BUILD_CHECK_INTERVAL_MS} and when the tab regains focus; call `onNewBuild` once,
 * the first time the server runs a different build, then stop. Returns the stop function.
 */
export function watchForNewBuild(p: {
  clientBuild: string;
  fetchServerBuild: () => Promise<string | null>;
  onNewBuild: (serverBuild: string) => void;
  /** Where focus is heard — the window. */
  target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
  now?: () => number;
  intervalMs?: number;
}): () => void {
  const now = p.now ?? Date.now;
  let stopped = false;
  let inFlight = false;
  let lastCheck = now();

  const check = async () => {
    if (stopped || inFlight) return;
    inFlight = true;
    lastCheck = now();
    try {
      const serverBuild = await p.fetchServerBuild();
      if (!stopped && serverBuild != null && isNewBuild(p.clientBuild, serverBuild)) {
        stop();
        p.onNewBuild(serverBuild);
      }
    } finally {
      inFlight = false;
    }
  };
  const onFocus = () => {
    if (now() - lastCheck >= FOCUS_MIN_GAP_MS) void check();
  };

  const timer = setInterval(() => void check(), p.intervalMs ?? BUILD_CHECK_INTERVAL_MS);
  p.target.addEventListener('focus', onFocus);
  function stop() {
    stopped = true;
    clearInterval(timer);
    p.target.removeEventListener('focus', onFocus);
  }
  return stop;
}
